import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import '../styles/fmp.css';
import { notificar } from '../utils/notify';
import OrcamentoProjecao, { ORCAMENTO_PROJECAO_CLIENTES } from '../components/orcamento/OrcamentoProjecao';

const formatarValor = (valor) => {
  if (!valor && valor !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(valor));
};
const fmtBR = (n) => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const parseBR = (v) => parseFloat(String(v || '').replace(/\./g, '').replace(',', '.')) || 0;
const formatarInputBRL = (value) => {
  let raw = value.replace(/[^\d,]/g, '');
  const parts = raw.split(',');
  let intPart = parts[0].replace(/^0+(?=\d)/, '');
  intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return parts.length > 1 ? `${intPart},${parts[1].slice(0, 2)}` : intPart;
};
const _ehUUID = (s) => typeof s === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s.trim());
const codCat = (codigo) => (_ehUUID(codigo) ? '' : (codigo || ''));
const MESES_NOMES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

/**
 * Página de cadastro de orçamento (Despesas ou Receitas), conforme prop `tipo`.
 * Mesma lógica do antigo modal, agora em página dedicada.
 */
export default function Orcamento_cadastro({ tipo = 'despesas' }) {
  const ehReceitas = tipo === 'receitas';
  const { cliente_id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const ano = parseInt(searchParams.get('ano') || new Date().getFullYear());
  // Orçamento com Monte Carlo (22/10/2026): só despesas de 25/27; abre na projeção por padrão
  const temProjecao = !ehReceitas && ORCAMENTO_PROJECAO_CLIENTES.has(String(cliente_id));
  const [modoTela, setModoTela] = useState(temProjecao ? 'projecao' : 'manual');
  useEffect(() => { setModoTela(temProjecao ? 'projecao' : 'manual'); }, [cliente_id, temProjecao]);
  // mantém os outros parâmetros da URL (ex.: consolidar=1 da projeção)
  const trocarAno = (novo) => setSearchParams((sp) => { const n = new URLSearchParams(sp); n.set('ano', String(novo)); return n; });

  const cor = ehReceitas ? '#1b5e20' : '#0d6efd';
  const btnCor = ehReceitas ? 'btn-success' : 'btn-primary';
  const outlineCor = ehReceitas ? 'btn-outline-success' : 'btn-outline-primary';

  const endpoints = {
    categorias: ehReceitas ? 'categorias-receitas-cliente' : 'categorias-orcamento-cliente',
    totaisAnoAnt: ehReceitas ? 'totais-ano-receitas-por-categoria' : 'totais-ano-por-categoria',
    anualTotal: ehReceitas ? 'orcamento-anual-receitas' : 'orcamento-anual',
  };

  const [nomeCliente, setNomeCliente] = useState('');
  const [categorias, setCategorias] = useState([]);
  const [periodo, setPeriodo] = useState('ano');
  const [mesSel, setMesSel] = useState(new Date().getMonth() + 1);
  const [distribuicao, setDistribuicao] = useState({});
  const [mesOrcamentos, setMesOrcamentos] = useState({});
  const [valoresMesCarregados, setValoresMesCarregados] = useState({});
  const [orcamentoTotal, setOrcamentoTotal] = useState('');
  const [orcamentoTotalMes, setOrcamentoTotalMes] = useState('');
  const [orcamentosAtuaisAnual, setOrcamentosAtuaisAnual] = useState({});
  const [totaisAnoAnterior, setTotaisAnoAnterior] = useState({});
  const [totalGeralAnoAnterior, setTotalGeralAnoAnterior] = useState(0);
  const [carregandoAnoAnterior, setCarregandoAnoAnterior] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);

  const headers = () => ({ 'Authorization': `Bearer ${localStorage.getItem('access')}` });
  const API = process.env.REACT_APP_API_BASE;

  const formatarValoresMes = (valoresMap) => {
    const result = {};
    Object.entries(valoresMap).forEach(([codigo, valor]) => {
      if (valor > 0) result[codigo] = fmtBR(valor);
    });
    return result;
  };

  const carregarValoresMes = async (mesAno, mesOrcamentosAtual) => {
    const calcMetaMensal = (valores) => {
      const soma = Object.values(valores).reduce((s, v) => s + parseBR(v), 0);
      if (soma > 0) return fmtBR(soma);
      const anualNum = parseBR(orcamentoTotal);
      if (anualNum > 0) return fmtBR(anualNum / 12);
      return '';
    };
    if (mesOrcamentosAtual[mesAno] !== undefined) {
      const cached = mesOrcamentosAtual[mesAno];
      setValoresMesCarregados(cached);
      setDistribuicao({ ...cached });
      setOrcamentoTotalMes(calcMetaMensal(cached));
      return mesOrcamentosAtual;
    }
    try {
      const res = await axios.get(`${API}/api/orcamento-mensal-categorias/`,
        { params: { cliente_id, mes_referencia: mesAno, tipo }, headers: headers() });
      const valores = (res.data.success && Object.keys(res.data.valores).length > 0)
        ? formatarValoresMes(res.data.valores) : {};
      const updated = { ...mesOrcamentosAtual, [mesAno]: valores };
      setMesOrcamentos(updated);
      setValoresMesCarregados(valores);
      setDistribuicao({ ...valores });
      setOrcamentoTotalMes(calcMetaMensal(valores));
      return updated;
    } catch (e) {
      console.error('Erro ao carregar valores do mês:', e);
      const updated = { ...mesOrcamentosAtual, [mesAno]: {} };
      setMesOrcamentos(updated);
      setValoresMesCarregados({});
      setDistribuicao({});
      setOrcamentoTotalMes('');
      return updated;
    }
  };

  const navegarMes = async (direcao) => {
    const mesAtualAno = `${ano}-${String(mesSel).padStart(2, '0')}`;
    const updated = { ...mesOrcamentos, [mesAtualAno]: distribuicao };
    setMesOrcamentos(updated);
    const novoMes = mesSel + direcao;
    const mesFinal = novoMes < 1 ? 12 : novoMes > 12 ? 1 : novoMes;
    const novoMesAno = `${ano}-${String(mesFinal).padStart(2, '0')}`;
    setMesSel(mesFinal);
    await carregarValoresMes(novoMesAno, updated);
  };

  const carregarTudo = useCallback(async () => {
    setCarregando(true);
    try {
      // cliente (nome)
      try {
        const rc = await axios.get(`${API}/api/cliente-bpo/${cliente_id}/`, { headers: headers() });
        if (rc.data && (rc.data.nome || rc.data.cliente?.nome)) setNomeCliente(rc.data.nome || rc.data.cliente.nome);
      } catch (e) { /* opcional */ }

      const rcat = await axios.get(`${API}/api/${endpoints.categorias}/`, { params: { cliente_id }, headers: headers() });
      if (rcat.data.success) setCategorias(rcat.data.categorias || []);

      const rAnual = await axios.get(`${API}/api/orcamento-anual-por-categoria/`,
        { params: { cliente_id, ano, tipo }, headers: headers() });
      if (rAnual.data.success) setOrcamentosAtuaisAnual(rAnual.data.valores || {});

      const rSalvo = await axios.get(`${API}/api/${endpoints.anualTotal}/`,
        { params: { cliente_id, ano }, headers: headers() });
      if (rSalvo.data.success && rSalvo.data.valor > 0) setOrcamentoTotal(fmtBR(rSalvo.data.valor));

      setCarregandoAnoAnterior(true);
      try {
        const rAnt = await axios.get(`${API}/api/${endpoints.totaisAnoAnt}/`,
          { params: { cliente_id, ano: ano - 1 }, headers: headers() });
        if (rAnt.data.success) {
          const map = {};
          (rAnt.data.totais || []).forEach(t => { map[t.codigo] = t.total; });
          setTotaisAnoAnterior(map);
          setTotalGeralAnoAnterior(rAnt.data.total_geral || 0);
        }
      } finally { setCarregandoAnoAnterior(false); }
    } catch (e) {
      console.error('Erro ao carregar cadastro de orçamento:', e);
    } finally {
      setCarregando(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cliente_id, ano, tipo]);

  useEffect(() => { if (cliente_id) carregarTudo(); }, [cliente_id, carregarTudo]);

  const salvar = async () => {
    setSalvando(true);
    try {
      const itens = [];
      if (periodo === 'mes') {
        const mesAtualAno = `${ano}-${String(mesSel).padStart(2, '0')}`;
        const todosMeses = { ...mesOrcamentos, [mesAtualAno]: distribuicao };
        for (const [mesRef, valores] of Object.entries(todosMeses)) {
          Object.entries(valores).filter(([_, v]) => v).forEach(([codigo, valor]) => {
            itens.push({ codigo_categoria: codigo, mes_referencia: mesRef, orcamento: parseBR(valor) });
          });
        }
      } else {
        Object.entries(distribuicao).filter(([_, v]) => v).forEach(([codigo, valor]) => {
          const valorMensal = Math.round((parseBR(valor) / 12) * 100) / 100;
          for (let mes = 1; mes <= 12; mes++) {
            itens.push({ codigo_categoria: codigo, mes_referencia: `${ano}-${String(mes).padStart(2, '0')}`, orcamento: valorMensal });
          }
        });
      }
      if (itens.length > 0) {
        await axios.post(`${API}/api/atualizar-orcamento-lote/`,
          { cliente_id, ano, tipo, periodo, itens }, { headers: headers() });
      }
      const totalNum = parseBR(orcamentoTotal);
      if (totalNum > 0 && periodo === 'ano') {
        await axios.post(`${API}/api/${endpoints.anualTotal}/`,
          { cliente_id, ano, valor: totalNum }, { headers: headers() });
      }
      setMensagem(`Orçamento de ${ehReceitas ? 'receitas' : 'despesas'} salvo com sucesso!`);
      await carregarTudo();
      setTimeout(() => setMensagem(''), 3500);
    } catch (e) {
      console.error('Erro ao salvar orçamento:', e);
      setMensagem('Erro ao salvar orçamento.');
      setTimeout(() => setMensagem(''), 4000);
    } finally {
      setSalvando(false);
    }
  };

  const totalStr = periodo === 'mes' ? orcamentoTotalMes : orcamentoTotal;
  const totalNum = parseBR(totalStr);
  const distribuido = periodo === 'mes'
    ? Object.values(valoresMesCarregados).reduce((s, v) => s + parseBR(v), 0)
    : Object.values(orcamentosAtuaisAnual).reduce((s, v) => s + (v || 0), 0);
  const restante = totalNum - distribuido;
  const pct = totalNum > 0 ? Math.min((distribuido / totalNum) * 100, 100) : 0;
  const corRestante = restante < 0 ? '#e74c3c' : restante === 0 ? '#27ae60' : '#2980b9';

  return (
    <div className="das-page">
      <div className="container-fluid mt-4 mb-5">
        {/* Cabeçalho */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div className="d-flex align-items-center gap-3">
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(`/Planejamento_orcamentario/${cliente_id}`)} title="Voltar ao Planejamento">
              <i className="fas fa-arrow-left me-1"></i>Voltar
            </button>
            <div className="d-flex align-items-center">
              <i className="fas fa-sliders-h header-icon me-2"></i>
              <h2 className="titulo-pagina m-0">
                Cadastrar Orçamento {ehReceitas ? 'de Receitas' : 'de Despesas'} — {ano}
              </h2>
            </div>
          </div>
          {nomeCliente && <span className="text-muted">{nomeCliente}</span>}
        </div>

        {/* Mensagens agora são exibidas como toast (react-toastify) */}

        {temProjecao && (
          <div className="d-flex gap-2 mb-3">
            <button className={`btn btn-sm ${modoTela === 'projecao' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setModoTela('projecao')}>
              <i className="fas fa-chart-area me-1"></i>Projeção (Monte Carlo)
            </button>
            <button className={`btn btn-sm ${modoTela === 'manual' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setModoTela('manual')}>
              <i className="fas fa-edit me-1"></i>Cadastro manual
            </button>
          </div>
        )}

        {temProjecao && modoTela === 'projecao' ? (
          <OrcamentoProjecao clienteId={cliente_id} ano={ano} onTrocarAno={trocarAno} />
        ) : carregando ? (
          <div className="text-center py-5"><span className="spinner-border text-primary"></span><p className="text-muted mt-2">Carregando...</p></div>
        ) : (
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              {/* Bloco do topo: período + meta + painel */}
              <div>
              {/* Período + meta */}
              <div className="row mb-4 align-items-end g-3">
                <div className="col-md-5">
                  <label className="form-label fw-bold">Período</label>
                  <div className="btn-group w-100">
                    <input type="radio" className="btn-check" id="periodo-ano" checked={periodo === 'ano'} onChange={() => setPeriodo('ano')} />
                    <label className={`btn ${outlineCor}`} htmlFor="periodo-ano"><i className="fas fa-calendar-alt me-2"></i>Ano Todo ({ano})</label>
                    <input type="radio" className="btn-check" id="periodo-mes" checked={periodo === 'mes'} onChange={async () => {
                      setPeriodo('mes');
                      await carregarValoresMes(`${ano}-${String(mesSel).padStart(2, '0')}`, mesOrcamentos);
                    }} />
                    <label className={`btn ${outlineCor}`} htmlFor="periodo-mes"><i className="fas fa-calendar-day me-2"></i>Mês Específico</label>
                  </div>
                </div>
                {periodo === 'mes' && (
                  <div className="col-md-2">
                    <label className="form-label fw-bold">Mês</label>
                    <select className="form-select" value={mesSel} onChange={async (e) => {
                      const novo = parseInt(e.target.value);
                      const updated = { ...mesOrcamentos, [`${ano}-${String(mesSel).padStart(2, '0')}`]: distribuicao };
                      setMesSel(novo);
                      await carregarValoresMes(`${ano}-${String(novo).padStart(2, '0')}`, updated);
                    }}>
                      {MESES_NOMES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                    </select>
                  </div>
                )}
                <div className="col-md-4">
                  <label className="form-label fw-bold">
                    <i className="fas fa-bullseye me-2" style={{ color: cor }}></i>
                    {periodo === 'mes' ? 'Meta do Mês' : (ehReceitas ? 'Meta Total a Distribuir' : 'Orçamento Total a Distribuir')}
                  </label>
                  <div className="input-group">
                    <span className="input-group-text fw-bold">R$</span>
                    <input type="text" className="form-control fs-5 fw-bold"
                      value={periodo === 'mes' ? orcamentoTotalMes : orcamentoTotal}
                      onChange={(e) => {
                        const f = formatarInputBRL(e.target.value);
                        periodo === 'mes' ? setOrcamentoTotalMes(f) : setOrcamentoTotal(f);
                      }}
                      placeholder="0,00" />
                  </div>
                  {carregandoAnoAnterior ? (
                    <small className="text-muted mt-1 d-block"><span className="spinner-border spinner-border-sm me-1"></span>Buscando ano anterior...</small>
                  ) : totalGeralAnoAnterior > 0 ? (
                    <small className="mt-1 d-block text-muted">
                      <i className="fas fa-history me-1"></i>Referência {ano - 1}:{' '}
                      <strong style={{ color: cor, cursor: 'pointer', textDecoration: 'underline dotted' }}
                        onClick={() => setOrcamentoTotal(fmtBR(totalGeralAnoAnterior))}>
                        {formatarValor(totalGeralAnoAnterior)}
                      </strong>
                      <span className="text-muted ms-1">(clique para usar)</span>
                    </small>
                  ) : null}
                </div>
              </div>

              {/* Painel distribuição */}
              <div className="card border-0 shadow-sm mb-4" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="card-body py-3">
                  <div className="row text-center mb-3">
                    <div className="col-4">
                      <small className="text-muted d-block">{periodo === 'mes' ? 'Meta do Mês' : (ehReceitas ? 'Meta Definida' : 'Orçamento Definido')}</small>
                      <strong className="fs-5" style={{ color: cor }}>{formatarValor(totalNum)}</strong>
                    </div>
                    <div className="col-4">
                      <small className="text-muted d-block">Distribuído</small>
                      <strong className="fs-5" style={{ color: ehReceitas ? '#1b5e20' : '#dc3545' }}>{formatarValor(distribuido)}</strong>
                      {periodo === 'mes' && (
                        <div className="d-flex align-items-center justify-content-center gap-2 mt-1">
                          <button className="btn btn-sm btn-outline-secondary py-0 px-2" style={{ fontSize: '16px' }} onClick={() => navegarMes(-1)}>‹</button>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#495057', whiteSpace: 'nowrap' }}>{MESES_NOMES[mesSel - 1]} {ano}</span>
                          <button className="btn btn-sm btn-outline-secondary py-0 px-2" style={{ fontSize: '16px' }} onClick={() => navegarMes(1)}>›</button>
                        </div>
                      )}
                    </div>
                    <div className="col-4">
                      <small className="text-muted d-block">Saldo Restante</small>
                      <strong style={{ color: corRestante }} className="fs-5">{formatarValor(restante)}</strong>
                    </div>
                  </div>
                  <div className="progress" style={{ height: '12px', borderRadius: '6px' }}>
                    <div className={`progress-bar ${restante < 0 ? 'bg-danger' : restante === 0 ? 'bg-success' : 'bg-primary'}`}
                      style={{ width: `${pct}%`, transition: 'width 0.3s', borderRadius: '6px' }}></div>
                  </div>
                  <div className="text-center mt-2">
                    <small className={`fw-bold ${restante < 0 ? 'text-danger' : restante === 0 ? 'text-success' : 'text-muted'}`}>
                      {restante < 0 ? `Excedido em ${formatarValor(Math.abs(restante))}`
                        : restante === 0 && totalNum > 0 ? (periodo !== 'mes' ? <><i className="fas fa-check-circle me-1"></i>Totalmente distribuído!</> : null)
                        : totalNum > 0 ? `Falta distribuir ${formatarValor(restante)}`
                        : `Informe a ${periodo === 'mes' ? 'meta do mês' : 'meta/orçamento'} acima`}
                    </small>
                  </div>
                </div>
              </div>

              </div>{/* fim bloco do topo */}

              {/* Tabela de categorias (rola por dentro; cabeçalho fixo) */}
              <div style={{ overflowY: 'auto', maxHeight: '55vh', minHeight: '320px' }}>
                <table className="table table-hover align-middle">
                  <thead className="table-dark" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th>Categoria</th>
                      <th className="text-end" width="160">
                        <i className="fas fa-history me-1" style={{ color: '#f39c12' }}></i>Real {ano - 1}
                        {carregandoAnoAnterior && <span className="spinner-border spinner-border-sm ms-2" style={{ width: '0.7rem', height: '0.7rem' }}></span>}
                      </th>
                      <th className="text-end" width="160">{periodo === 'mes' ? 'Orç. Atual Mensal' : 'Orç. Atual Anual'}</th>
                      <th className="text-end" width="200">{periodo === 'mes' ? 'Novo Valor Mensal' : 'Novo Valor Anual'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categorias.map((cat) => {
                      const valorAtual = distribuicao[cat.codigo] || '';
                      const realAnoAntTotal = totaisAnoAnterior[cat.codigo] || 0;
                      const realAnoAnt = periodo === 'mes' ? realAnoAntTotal / 12 : realAnoAntTotal;
                      return (
                        <tr key={cat.codigo}>
                          <td>
                            <strong style={{ color: cor }}>{codCat(cat.codigo)}</strong>
                            <span className="text-dark ms-2">{cat.descricao}</span>
                          </td>
                          <td className="text-end">
                            {realAnoAnt > 0 ? (
                              <span style={{ color: '#e67e22', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline dotted' }}
                                title={periodo === 'mes' ? 'Média mensal do ano anterior — clique para usar' : 'Clique para usar como novo valor'}
                                onClick={() => setDistribuicao(prev => ({ ...prev, [cat.codigo]: fmtBR(realAnoAnt) }))}>
                                {formatarValor(realAnoAnt)}
                              </span>
                            ) : <span className="text-muted" style={{ fontSize: '12px' }}>—</span>}
                          </td>
                          <td className="text-end">
                            <span style={{ color: '#27ae60', fontWeight: 600 }}>
                              {periodo === 'mes'
                                ? formatarValor(parseBR(valoresMesCarregados[cat.codigo] || '0'))
                                : formatarValor(orcamentosAtuaisAnual[cat.codigo] || 0)}
                            </span>
                          </td>
                          <td className="text-end">
                            <div className="input-group input-group-sm" style={{ maxWidth: '180px', marginLeft: 'auto' }}>
                              <span className="input-group-text">R$</span>
                              <input type="text" className="form-control text-end" value={valorAtual}
                                onChange={(e) => setDistribuicao(prev => ({ ...prev, [cat.codigo]: formatarInputBRL(e.target.value) }))}
                                placeholder="0,00" />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card-footer d-flex justify-content-end gap-2 bg-white">
              <button className="btn btn-secondary" onClick={() => navigate(`/Planejamento_orcamentario/${cliente_id}`)} disabled={salvando}>
                <i className="fas fa-times me-2"></i>Cancelar
              </button>
              <button className={`btn ${btnCor}`} onClick={salvar} disabled={salvando || Object.keys(distribuicao).length === 0}>
                {salvando ? <><span className="spinner-border spinner-border-sm me-2"></span>Salvando...</>
                  : <><i className="fas fa-save me-2"></i>Salvar Orçamento</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
